import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileText, Search } from 'lucide-react';
import { ROUTES } from '@/app/router/routes';
import { NAVIGATION } from '@/app/router/modules';
import { useAuth } from '@/features/auth/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { searchApi } from '@/services/api/endpoints/search';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';
import { SEARCH_MIN_QUERY_LENGTH, type SearchHit } from '@/types/search';
import { Spinner } from './Spinner';

/**
 * ⌘K search.
 *
 * Pages are matched locally, as before. With SEARCH_USE and at least two typed
 * characters the palette also asks the server's global search for records, and
 * lists them under the page matches, grouped by type. The server decides which
 * types the caller may see; the frontend only decides which screen opens a hit.
 */

const SEARCH_DEBOUNCE_MS = 250;

/** The screen that opens a hit. Unknown types have none and render without a link. */
function pathForHit(hit: SearchHit): string | null {
  switch (hit.type) {
    case 'students':
      return ROUTES.studentDetail(hit.id);
    case 'guardians':
      return ROUTES.guardianDetail(hit.id);
    case 'employees':
      return ROUTES.employees;
    case 'invoices':
      return ROUTES.invoiceDetail(hit.id);
    case 'books':
      return ROUTES.bookDetail(hit.id);
    case 'vehicles':
      return ROUTES.vehicleDetail(hit.id);
    case 'routes':
      return ROUTES.transportRouteDetail(hit.id);
    case 'assets':
      return ROUTES.assetDetail(hit.id);
    case 'admissions':
      return ROUTES.admissionDetail(hit.id);
    default:
      return null;
  }
}

/** One keyboard-reachable row: a page or a linked record. */
interface NavigableItem {
  key: string;
  path: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { can, canAny } = useAuth();
  const handleError = useApiErrorHandler();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const canSearch = can(PERMISSIONS.SEARCH_USE);
  const term = query.trim();
  const debouncedTerm = useDebouncedValue(term, SEARCH_DEBOUNCE_MS);
  const searching = open && canSearch && debouncedTerm.length >= SEARCH_MIN_QUERY_LENGTH;
  // Records are shown only while the typed text is itself long enough.
  const showRecords = canSearch && term.length >= SEARCH_MIN_QUERY_LENGTH;

  const pages = useMemo(
    () =>
      NAVIGATION.flatMap((section) =>
        section.items
          .filter((item) => canAny(item.permissions))
          .map((item) => ({ label: item.label, section: section.title, path: item.path })),
      ),
    [canAny],
  );

  const pageHits = useMemo(() => {
    const lowered = term.toLowerCase();
    const matches =
      lowered === ''
        ? pages
        : pages.filter(
            (page) =>
              page.label.toLowerCase().includes(lowered) ||
              page.section.toLowerCase().includes(lowered),
          );
    return matches.slice(0, 8);
  }, [pages, term]);

  const records = useQuery({
    queryKey: queryKeys.search.query(debouncedTerm),
    queryFn: () => searchApi.search(debouncedTerm),
    enabled: searching,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (records.error) {
      handleError(records.error);
    }
  }, [records.error, handleError]);

  const groups = useMemo(
    () => (showRecords ? (records.data?.groups ?? []) : []),
    [showRecords, records.data],
  );
  const recordFetching = showRecords && (records.isFetching || term !== debouncedTerm);
  const anyRecordHit = groups.some((group) => group.hits.length > 0);
  const anyUnavailable = groups.some((group) => group.unavailable);
  const recordsSettled = showRecords && !recordFetching && records.data !== undefined && !records.isError;

  // Pages first, then linked record hits, in display order — one continuous list.
  const navigable = useMemo<NavigableItem[]>(() => {
    const items: NavigableItem[] = pageHits.map((page) => ({ key: `page:${page.path}`, path: page.path }));
    for (const group of groups) {
      for (const hit of group.hits) {
        const path = pathForHit(hit);
        if (path !== null) {
          items.push({ key: `hit:${group.type}:${hit.id}`, path });
        }
      }
    }
    return items;
  }, [pageHits, groups]);

  const indexByKey = useMemo(() => {
    const map = new Map<string, number>();
    navigable.forEach((item, index) => map.set(item.key, index));
    return map;
  }, [navigable]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Focus after paint, or the input is not in the document yet.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the index in range when record results arrive or shrink.
  useEffect(() => {
    if (active >= navigable.length && navigable.length > 0) {
      setActive(navigable.length - 1);
    }
  }, [active, navigable.length]);

  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>(`[data-nav-index="${active}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) {
    return null;
  }

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const count = navigable.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (count === 0 ? 0 : (index + 1) % count));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (count === 0 ? 0 : (index - 1 + count) % count));
    } else if (event.key === 'Enter' && navigable[active] !== undefined) {
      event.preventDefault();
      go(navigable[active].path);
    }
  };

  const rowClass = (highlighted: boolean) =>
    `flex w-full items-center gap-2.5 border-b border-line px-4 py-[11px] text-left transition-colors ${
      highlighted ? 'bg-surface' : ''
    }`;

  const nothingAtAll = pageHits.length === 0 && !showRecords;
  const label = canSearch ? 'Search' : 'Search pages';

  return (
    <div
      className="animate-fade-in fixed inset-0 z-[60] grid items-start justify-center px-4 pb-4 pt-[12vh]"
      style={{ background: 'var(--sms-overlay)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="animate-rise flex max-h-[76vh] w-full max-w-[560px] flex-col overflow-hidden rounded-xl border border-line bg-card shadow-lg"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        <div className="flex items-center gap-2.5 border-b-2 border-line px-4 py-3.5">
          <Search className="h-[17px] w-[17px] shrink-0 text-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canSearch ? 'Search pages and records…' : 'Search pages…'}
            aria-label={label}
            className="min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ink outline-none placeholder:text-subtle"
          />
          {recordFetching && <Spinner className="h-4 w-4 shrink-0 text-muted" />}
          <span className="border border-line px-1.5 text-[11px] text-muted">ESC</span>
        </div>

        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {nothingAtAll ? (
            <div className="px-4 py-8">
              <strong className="text-[15px] font-extrabold text-ink">No matches</strong>
              <p className="mt-1.5 text-[13px] text-muted">
                Nothing here matches “{query}”. Try a page name such as Students or Timetables.
              </p>
            </div>
          ) : (
            <>
              {pageHits.length > 0 && (
                <ul>
                  {pageHits.map((page) => {
                    const index = indexByKey.get(`page:${page.path}`) ?? -1;
                    return (
                      <li key={page.path}>
                        <button
                          type="button"
                          data-nav-index={index}
                          onMouseEnter={() => setActive(index)}
                          onClick={() => go(page.path)}
                          className={rowClass(index === active)}
                        >
                          <ArrowRight className="h-[15px] w-[15px] shrink-0 text-muted" aria-hidden="true" />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-ink">{page.label}</span>
                            <span className="block truncate text-xs text-muted">{page.section}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {showRecords && (
                <>
                  {groups.map((group) =>
                    group.hits.length === 0 && !group.unavailable ? null : (
                      <section key={group.type} aria-label={group.label}>
                        <div className="border-b border-line bg-surface px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-muted">
                          {group.label}
                        </div>
                        {group.unavailable && (
                          <p className="border-b border-line px-4 py-[11px] text-[13px] text-muted">
                            Couldn't search {group.label} just now
                          </p>
                        )}
                        <ul>
                          {group.hits.map((hit) => {
                            const path = pathForHit(hit);
                            const body = (
                              <>
                                <FileText className="h-[15px] w-[15px] shrink-0 text-muted" aria-hidden="true" />
                                <span className="min-w-0">
                                  <span className="block truncate text-sm font-semibold text-ink">{hit.title}</span>
                                  {hit.subtitle && (
                                    <span className="block truncate text-xs text-muted">{hit.subtitle}</span>
                                  )}
                                </span>
                              </>
                            );
                            if (path === null) {
                              return (
                                <li key={`${group.type}:${hit.id}`} className={rowClass(false)}>
                                  {body}
                                </li>
                              );
                            }
                            const index = indexByKey.get(`hit:${group.type}:${hit.id}`) ?? -1;
                            return (
                              <li key={`${group.type}:${hit.id}`}>
                                <button
                                  type="button"
                                  data-nav-index={index}
                                  onMouseEnter={() => setActive(index)}
                                  onClick={() => go(path)}
                                  className={rowClass(index === active)}
                                >
                                  {body}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    ),
                  )}

                  {records.isError && !recordFetching && (
                    <p className="border-b border-line px-4 py-[11px] text-[13px] text-muted">
                      Couldn't search records just now.
                    </p>
                  )}

                  {recordsSettled && !anyRecordHit && !anyUnavailable && (
                    <div className="px-4 py-6">
                      <strong className="text-[15px] font-extrabold text-ink">No records match</strong>
                      <p className="mt-1.5 text-[13px] text-muted">Nothing on record matches “{term}”.</p>
                    </div>
                  )}

                  {recordFetching && groups.length === 0 && (
                    <p className="flex items-center gap-2 px-4 py-[11px] text-[13px] text-muted">
                      <Spinner className="h-4 w-4" /> Searching records…
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="bg-surface px-4 py-2 text-[11.5px] text-muted">
          Enter to open · ↑↓ to move · ⌘K to toggle
        </div>
      </div>
    </div>
  );
}
